import { createAsyncThunk } from '@reduxjs/toolkit';
import { BibleQuote, QueryParams } from '../../types';
import { Paginated, PaginationService } from '../../services/api-handlers/pagination';
import { resolveApiCall } from '../../services/api-handlers/api-resolver';
import { BibleQuoteApi } from '../../services/end-points';
import SortingService from '../../services/sorting';
import { makeFormErrorsFromResponse } from '../../components/react-hook-form/utils/make-form-errors';
import { BibleQuoteFormData } from '../../screens/BibleQuote/components/BibleQuoteForm';
import { ThunkConfig } from '../configure-store';

export const listBibleQuotes = createAsyncThunk<Paginated<BibleQuote>, QueryParams, ThunkConfig>(
    'bible-quote/list',
    async (payload, thunkApi) => {
        const { getState } = thunkApi;
        const { bibleQuote } = getState();
        const { filters, sorting, ...rest } = payload;

        return PaginationService.resolveApiCall(
            thunkApi,
            bibleQuote,
            async () =>
                BibleQuoteApi.list({
                    ...filters,
                    ...SortingService.makeOrder(sorting),
                    ...rest,
                }),
            payload.page,
            bibleQuote.data,
        );
    },
);

export const getAllBibleQuotes = createAsyncThunk<Paginated<BibleQuote>, undefined, ThunkConfig>(
    'bible-quote/all-list',
    async (payload, thunkAPI) => {
        const { getState } = thunkAPI;
        const { bibleQuote } = getState();

        return PaginationService.resolveApiCall(
            thunkAPI,
            bibleQuote,
            async () =>
                BibleQuoteApi.list({
                    limit: -1,
                }),
            1,
            {
                list: [],
                page: 1,
            },
        );
    },
);

export const getBibleQuote = createAsyncThunk<BibleQuote, { id: string; onError: (data: any) => any }, ThunkConfig>(
    'bible-quote/current',
    async (payload, thunkApi) => {
        const { getState } = thunkApi;
        const { bibleQuote } = getState();
        const { id, onError } = payload;

        return resolveApiCall(thunkApi, bibleQuote, async () => (await BibleQuoteApi.get(id)).data, onError);
    },
);

export const createBibleQuote = createAsyncThunk<BibleQuote, BibleQuoteFormData, ThunkConfig>(
    'bible-quote/create',
    async (payload, thunkAPI) => {
        const { getState } = thunkAPI;
        const { bibleQuote } = getState();
        return resolveApiCall(
            thunkAPI,
            bibleQuote,
            async () => await BibleQuoteApi.create(payload),
            async (err) => {
                const { response } = err;
                return makeFormErrorsFromResponse(response.data);
            },
        );
    },
);

export const deleteBibleQuote = createAsyncThunk<any, { id: string; callback: () => void }, ThunkConfig>(
    'bible-quote/delete',
    async (payload, thunkApi) => {
        const { getState } = thunkApi;
        const { id } = payload;
        const { bibleQuote } = getState();
        await resolveApiCall(
            thunkApi,
            bibleQuote,
            async () => (
                await BibleQuoteApi.remove(id),
                async (err: { response: any }) => {
                    const { response } = err;
                    return makeFormErrorsFromResponse(response.data);
                }
            ),
        );
        payload.callback();
    },
);

export const updateBibleQuote = createAsyncThunk<
    BibleQuote,
    { id: BibleQuote['id']; data: Partial<BibleQuoteFormData> },
    ThunkConfig
>('bible-quote/update', async (payload, thunkApi) => {
    const { getState } = thunkApi;
    const { bibleQuote } = getState();
    const { id, data } = payload;
    return resolveApiCall(
        thunkApi,
        bibleQuote,
        async () => await BibleQuoteApi.update(id, data),
        async (err) => {
            const { response } = err;
            return makeFormErrorsFromResponse(response.data);
        },
    );
});
